import React, { useContext, useEffect, useState } from 'react'
import { PeFilterContext } from '../../Contexts/PeFilterContext'
import Popup from '../Common/Popup'

/**
 * The `DealRangeFilter` function is a React component that renders a form input for selecting a deal
 * range in US dollars.
 * @returns a JSX element, which represents a form input for filtering a deal range.
 */
function DealRangeFilter({ customPathname, DealRange, setDealRange, label = "Deal Range (US$ M)", isPopupOpen, setIsPopupOpen }) {

    const { refineSearchFilters, accessControl } = useContext(PeFilterContext);
    const [internalPopupOpen, setInternalPopupOpen] = useState(false);
    const popupOpen = isPopupOpen !== undefined ? isPopupOpen : internalPopupOpen;
    const setPopupOpen = setIsPopupOpen !== undefined ? setIsPopupOpen : setInternalPopupOpen;
    const [error, setError] = useState("");

    const minValue = accessControl && accessControl.pe_deal_range !== null ? accessControl.pe_deal_range.from : 0;
    const maxValue = accessControl && accessControl.pe_deal_range !== null ? accessControl.pe_deal_range.to : 20000;

    useEffect(() => {
        const from = refineSearchFilters[customPathname]?.dealRange?.at(0)?.name ?? "";
        const to = refineSearchFilters[customPathname]?.dealRange?.at(1)?.name ?? "";
        setDealRange({ from, to })
    }, [customPathname, refineSearchFilters, setDealRange])

    const handleChange = (e) => {
        const { name, value } = e.target;
        const formatedValue = value.replace(/[^0-9]/g, '')
        setDealRange(prev => ({ ...prev, [name]: formatedValue }))
        // Clear error when user starts typing
        if (error) setError("");
    }

    // Validate when one field has value but other is empty
    useEffect(() => {
        const hasFrom = DealRange?.from !== "" && DealRange?.from !== null;
        const hasTo = DealRange?.to !== "" && DealRange?.to !== null;
        
        if (hasFrom && !hasTo) {
            // Auto-set To value to maxValue when From is entered but To is empty
            setDealRange(prev => ({ ...prev, to: maxValue.toString() }));
        } else if (!hasFrom && hasTo) {
            setError("Please enter From value");
        } else if (hasFrom && hasTo && parseFloat(DealRange.from) > parseFloat(DealRange.to)) {
            setError("From value must be less than To value");
        } else {
            setError("");
        }
    }, [DealRange?.from, DealRange?.to, maxValue]);


    const getDisplayText = () => {
        if (DealRange?.from && DealRange?.to) {
            return `: ${DealRange.from} to ${DealRange.to}`;
        } else if (DealRange?.from) {
            return `: From ${DealRange.from}`;
        } else if (DealRange?.to) {
            return `: Up to ${DealRange.to}`;
        }
        return " ";
    };

    return (
        <div className="relative w-full md:inline-block">
            <button
                type="button"
                className="h-10 rounded-md px-4 py-1 text-gray-500 hover:bg-gray-100  text-sm shadow-md border border-gray-300 text-left flex items-center justify-between w-full md:w-[90%]"
                onClick={() => setPopupOpen(!popupOpen)}
            >
                <span className="truncate pr-2">{label}</span>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {(DealRange?.from || DealRange?.to) && (
                        <span className="bg-white rounded-full px-2 py-0.5 text-xs font-medium border border-gray-400">
                            {getDisplayText()}
                        </span>
                    )}
                    <span className={`transition-transform duration-200 text-xl font-semibold ${popupOpen ? 'rotate-[270deg]' : 'rotate-90'}`}>
                        ›
                    </span>
                </div>
            </button>

            {popupOpen && (
                <Popup onClose={() => setPopupOpen(false)}>
                    <div className="p-4 min-w-[200px]">
                        
                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                    From (US$ M)
                                </label>
                                <input 
                                    type="number" 
                                    min={minValue} 
                                    name="from" 
                                    onChange={handleChange}
                                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 ${
                                        error && (!DealRange?.from || parseFloat(DealRange.from) > parseFloat(DealRange.to))
                                            ? 'border-red-500 focus:ring-2 focus:ring-red-500'
                                            : 'border-gray-300 focus:ring-[#BC8B20]'
                                    }`}
                                    value={DealRange?.from ?? ""} 
                                    placeholder={minValue.toString()} 
                                    autoComplete='off' 
                                />
                            </div>
                            
                            <div className="mb-3">
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                    To (US$ M)
                                </label>
                                <input 
                                    type="number" 
                                    min={DealRange?.from ?? "1"} 
                                    name="to" 
                                    onChange={handleChange} 
                                    max={maxValue}
                                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 ${
                                        error && (!DealRange?.to || parseFloat(DealRange.from) > parseFloat(DealRange.to))
                                            ? 'border-red-500 focus:ring-2 focus:ring-red-500'
                                            : 'border-gray-300 focus:ring-[#BC8B20]'
                                    }`}
                                    value={DealRange?.to ?? ""} 
                                    placeholder={maxValue.toString()} 
                                    autoComplete='off' 
                                />
                            </div>
                            
                            {error && (
                                <div className="text-red-600 text-xs font-medium mt-2">
                                    {error}
                                </div>
                            )}
                            </div>
                        </div>
                </Popup>
            )}
        </div>
    )
}

export default DealRangeFilter